/**
 * MpCheckoutModal — keeps the user "inside Lumfi" while MercadoPago
 * handles the actual checkout in a popup window.
 *
 * Why a popup (not an iframe)? MercadoPago blocks framing of its
 * hosted checkout via X-Frame-Options + CSP for security. The only
 * viable in-app pattern for preapproval/subscriptions is to open MP
 * in a child window and poll our backend until the webhook confirms
 * the new status. Same pattern Stripe Checkout and Shopify Pay use.
 *
 * Flow:
 *   1. Render: open MP `init_point` via window.open in a sized popup.
 *   2. Two parallel watchers run on a 2s interval:
 *      - `composition/me` poll — flips state to ✅ when MP webhook // design-system: ALLOW user-copy
 *        marks the composition as `active`.
 *      - `popup.closed` check — if the user closes MP without paying,
 *        offer "Reabrir MercadoPago" + "Cancelar".
 *   3. On `active` detection → close popup, fire `onSuccess(comp)`
 *      so the parent can navigate to the return page.
 *   4. Auto-timeout after 15 minutes (MP preapproval usually expires
 *      well after the user has either paid or abandoned).
 *
 * Props:
 *   - open: boolean
 *   - initPoint: string (MP checkout URL)
 *   - compositionId: string (used as freshness marker for polling)
 *   - onClose: () => void
 *   - onSuccess: (composition) => void  — fired when status becomes "active"
 *   - isEs: boolean
 *   - returnUrl: string  — surfaced on the success screen ("ver detalle")
 */
import { useEffect, useRef, useState, useCallback } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { CheckCircle2, Loader2, ExternalLink, AlertTriangle, X } from "lucide-react";
import { api } from "../lib/api";

const POLL_INTERVAL_MS = 2000;
const TIMEOUT_MS = 15 * 60 * 1000; // 15 minutes

export function MpCheckoutModal({
  open,
  initPoint,
  compositionId,
  onClose,
  onSuccess,
  isEs,
  returnUrl,
}) {
  // "opening" → just clicked Pay, opening popup
  // "waiting" → popup open, polling for confirmation
  // "popup-blocked" → window.open returned null
  // "popup-closed" → user closed popup without confirming
  // "success" → composition active, ready to redirect
  // "timeout" → 15min elapsed without confirmation
  // "error" → fetch error during polling
  const [phase, setPhase] = useState("opening");
  const phaseRef = useRef("opening");
  // Keep a ref in sync with `phase` so the interval callback (whose
  // closure captures the *initial* render's `phase`) always sees the
  // freshest value when deciding popup-closed transitions.
  useEffect(() => { phaseRef.current = phase; }, [phase]);
  const [errMsg, setErrMsg] = useState("");
  const popupRef = useRef(null);
  const pollTimerRef = useRef(null);
  const timeoutTimerRef = useRef(null);
  const closedAtRef = useRef(null);

  const stopTimers = useCallback(() => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    if (timeoutTimerRef.current) {
      clearTimeout(timeoutTimerRef.current);
      timeoutTimerRef.current = null;
    }
  }, []);

  const openMpPopup = useCallback(() => {
    if (!initPoint) return;
    // Centered popup. Some browsers ignore size on mobile and open a
    // tab instead — that's fine, polling still works.
    const width = 520;
    const height = 760;
    const left = Math.max(0, Math.round((window.screen.width - width) / 2));
    const top = Math.max(0, Math.round((window.screen.height - height) / 2));
    const features = [
      `width=${width}`,
      `height=${height}`,
      `left=${left}`,
      `top=${top}`,
      "resizable=yes",
      "scrollbars=yes",
      "noopener=no", // we need the ref to detect close
    ].join(",");
    const w = window.open(initPoint, "lumfi-mp-checkout", features);
    if (!w) {
      setPhase("popup-blocked");
      return;
    }
    popupRef.current = w;
    closedAtRef.current = null;
    setPhase("waiting");
    try { w.focus(); } catch (_) { /* cross-origin focus rejected */ }
  }, [initPoint]);

  const pollComposition = useCallback(async () => {
    try {
      const r = await api.get("/billing/composition/me");
      const comp = r?.data?.composition;
      if (!comp) return;
      // Only react to OUR composition (avoid races where /me returns
      // an older active row from a previous subscription).
      if (compositionId && comp.id !== compositionId) return;
      if (comp.status === "active") {
        stopTimers();
        setPhase("success");
        try { popupRef.current?.close(); } catch (_) { /* noop */ }
        // Give the success state a beat to render before parent navigates.
        setTimeout(() => onSuccess?.(comp), 600);
      }
    } catch (e) {
      // Transient network errors are OK; only surface if persistent.
      // We rely on the timeout to eventually give up.
      setErrMsg(e?.response?.data?.detail || e?.message || "");
    }
  }, [compositionId, onSuccess, stopTimers]);

  // Mount / unmount lifecycle.
  useEffect(() => {
    if (!open) return;
    openMpPopup();

    pollTimerRef.current = setInterval(() => {
      // Watch the popup window state.
      const w = popupRef.current;
      if (w && w.closed && phaseRef.current === "waiting") {
        // Grace period: many users close the popup AFTER MP confirms
        // the charge but BEFORE our webhook fires. Wait a beat and
        // double-check the composition status before declaring abandon.
        if (!closedAtRef.current) {
          closedAtRef.current = Date.now();
        } else if (Date.now() - closedAtRef.current > 3500) {
          setPhase("popup-closed");
          // One last poll in case the webhook just arrived.
          pollComposition();
          stopTimers();
          return;
        }
      }
      // Always poll the backend — if the webhook fired while the
      // popup was open, the user shouldn't have to close it manually.
      pollComposition();
    }, POLL_INTERVAL_MS);

    timeoutTimerRef.current = setTimeout(() => {
      setPhase((p) => (p === "waiting" ? "timeout" : p));
      stopTimers();
    }, TIMEOUT_MS);

    return () => {
      stopTimers();
      // Don't auto-close popup on unmount — the user might still be
      // mid-payment. Closing the modal is intentional via onClose.
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleClose = useCallback(() => {
    stopTimers();
    try { popupRef.current?.close(); } catch (_) { /* noop */ }
    onClose?.();
  }, [onClose, stopTimers]);

  const handleReopen = useCallback(() => {
    closedAtRef.current = null;
    setPhase("opening");
    openMpPopup();
    // Restart polling.
    if (!pollTimerRef.current) {
      pollTimerRef.current = setInterval(() => pollComposition(), POLL_INTERVAL_MS);
      timeoutTimerRef.current = setTimeout(() => {
        setPhase((p) => (p === "waiting" ? "timeout" : p));
        stopTimers();
      }, TIMEOUT_MS);
    }
  }, [openMpPopup, pollComposition, stopTimers]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent
        className="sm:max-w-md p-0 overflow-hidden"
        data-testid="mp-checkout-modal"
      >
        <div className="relative bg-gradient-to-br from-slate-50 to-white p-6">
          <button /* design-system: ALLOW legacy-todo */
            type="button"
            onClick={handleClose}
            className="absolute top-3 right-3 p-1 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition"
            data-testid="mp-checkout-close"
            aria-label={isEs ? "Cerrar" : "Close"}
          >
            <X className="h-4 w-4" />
          </button>

          <div className="flex items-center gap-2.5 mb-1">
            <div className="h-7 w-7 rounded-lg bg-gradient-to-br from-sky-500 to-blue-600 flex items-center justify-center text-white text-[10px] font-bold">
              MP
            </div>
            <DialogTitle className="text-base font-semibold text-slate-900">
              {phase === "success"
                ? (isEs ? "¡Pago confirmado!" : "Payment confirmed!")
                : (isEs ? "Pago seguro con MercadoPago" : "Secure payment with MercadoPago")}
            </DialogTitle>
          </div>
          <DialogDescription className="text-xs text-slate-500">
            {phase === "success"
              ? (isEs ? "Tu suscripción está activa." : "Your subscription is now active.")
              : (isEs
                  ? "Estás pagando dentro de Lumfi. No cierres esta ventana."
                  : "You're paying inside Lumfi. Don't close this window.")}
          </DialogDescription>
        </div>

        <div className="p-6 pt-2 min-h-[200px] flex flex-col items-center justify-center">
          {phase === "opening" && (
            <div className="flex flex-col items-center gap-3 text-slate-600" data-testid="mp-checkout-opening">
              <Loader2 className="h-7 w-7 animate-spin text-sky-600" />
              <p className="text-sm">
                {isEs ? "Abriendo MercadoPago…" : "Opening MercadoPago…"}
              </p>
            </div>
          )}

          {phase === "waiting" && (
            <div className="flex flex-col items-center gap-3 text-center" data-testid="mp-checkout-waiting">
              <Loader2 className="h-7 w-7 animate-spin text-sky-600" />
              <div>
                <p className="text-sm text-slate-700 font-medium">
                  {isEs ? "Esperando confirmación de MercadoPago…" : "Waiting for MercadoPago confirmation…"}
                </p>
                <p className="text-xs text-slate-500 mt-1.5 max-w-xs">
                  {isEs
                    ? "Completá el pago en la ventana de MP. Esta pantalla se actualiza sola."
                    : "Complete the payment in the MP window. This screen updates automatically."}
                </p>
              </div>
              <button /* design-system: ALLOW legacy-todo */
                type="button"
                onClick={() => {
                  try { popupRef.current?.focus(); } catch (_) { /* noop */ }
                }}
                className="text-[11px] text-sky-700 hover:text-sky-900 inline-flex items-center gap-1 mt-1"
                data-testid="mp-checkout-focus-popup"
              >
                <ExternalLink className="h-3 w-3" />
                {isEs ? "Volver a la ventana de MercadoPago" : "Bring MercadoPago window to front"}
              </button>
            </div>
          )}

          {phase === "popup-blocked" && (
            <div className="flex flex-col items-center gap-3 text-center" data-testid="mp-checkout-popup-blocked">
              <AlertTriangle className="h-7 w-7 text-amber-600" />
              <div>
                <p className="text-sm text-slate-700 font-medium">
                  {isEs ? "Tu navegador bloqueó el popup" : "Your browser blocked the popup"}
                </p>
                <p className="text-xs text-slate-500 mt-1.5 max-w-xs">
                  {isEs
                    ? "Permite popups para Lumfi o abre MercadoPago en una pestaña nueva."
                    : "Allow popups for Lumfi or open MercadoPago in a new tab."}
                </p>
              </div>
              <a
                href={initPoint}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-sm font-medium text-white bg-sky-600 hover:bg-sky-700 px-4 py-2 rounded-lg transition"
                data-testid="mp-checkout-open-tab"
                onClick={() => {
                  // Switch to waiting after opening in new tab so polling continues.
                  setPhase("waiting");
                  if (!pollTimerRef.current) {
                    pollTimerRef.current = setInterval(() => pollComposition(), POLL_INTERVAL_MS);
                    timeoutTimerRef.current = setTimeout(() => {
                      setPhase((p) => (p === "waiting" ? "timeout" : p));
                      stopTimers();
                    }, TIMEOUT_MS);
                  }
                }}
              >
                <ExternalLink className="h-3.5 w-3.5" />
                {isEs ? "Abrir MercadoPago" : "Open MercadoPago"}
              </a>
            </div>
          )}

          {phase === "popup-closed" && (
            <div className="flex flex-col items-center gap-3 text-center" data-testid="mp-checkout-popup-closed">
              <AlertTriangle className="h-7 w-7 text-amber-600" />
              <div>
                <p className="text-sm text-slate-700 font-medium">
                  {isEs ? "Cerraste la ventana de MercadoPago" : "You closed the MercadoPago window"}
                </p>
                <p className="text-xs text-slate-500 mt-1.5 max-w-xs">
                  {isEs
                    ? "Si ya pagaste, dame un segundo más. Si no, puedes reabrir MercadoPago."
                    : "If you already paid, give me a moment. Otherwise reopen MercadoPago."}
                </p>
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={handleReopen}
                  data-testid="mp-checkout-reopen"
                  className="bg-sky-600 hover:bg-sky-700"
                >
                  {isEs ? "Reabrir MercadoPago" : "Reopen MercadoPago"}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleClose}
                  data-testid="mp-checkout-cancel"
                >
                  {isEs ? "Cerrar" : "Close"}
                </Button>
              </div>
            </div>
          )}

          {phase === "timeout" && (
            <div className="flex flex-col items-center gap-3 text-center" data-testid="mp-checkout-timeout">
              <AlertTriangle className="h-7 w-7 text-rose-600" />
              <div>
                <p className="text-sm text-slate-700 font-medium">
                  {isEs ? "No detectamos confirmación de MercadoPago" : "We didn't detect MercadoPago confirmation"}
                </p>
                <p className="text-xs text-slate-500 mt-1.5 max-w-xs">
                  {isEs
                    ? "Si pagaste, refrescá Lumfi en unos minutos — los webhooks de MP pueden demorar."
                    : "If you paid, refresh Lumfi in a few minutes — MP webhooks can be slow."}
                </p>
              </div>
              <div className="flex gap-2">
                <Button size="sm" onClick={handleReopen} data-testid="mp-checkout-reopen">
                  {isEs ? "Reintentar" : "Retry"}
                </Button>
                <Button size="sm" variant="outline" onClick={handleClose} data-testid="mp-checkout-close-timeout">
                  {isEs ? "Cerrar" : "Close"}
                </Button>
              </div>
            </div>
          )}

          {phase === "success" && (
            <div className="flex flex-col items-center gap-3 text-center" data-testid="mp-checkout-success">
              <div className="rounded-full bg-emerald-100 p-2">
                <CheckCircle2 className="h-8 w-8 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-slate-800 font-semibold">
                  {isEs ? "Suscripción activa" : "Subscription active"}
                </p>
                <p className="text-xs text-slate-500 mt-1.5 max-w-xs">
                  {isEs ? "Redirigiendo a tu resumen…" : "Redirecting to your summary…"}
                </p>
              </div>
            </div>
          )}

          {errMsg && phase === "waiting" && (
            <p className="text-[10px] text-rose-600 mt-3" data-testid="mp-checkout-error">
              {errMsg}
            </p>
          )}
        </div>

        <div className="border-t border-slate-200 px-5 py-3 bg-slate-50/70 text-[10px] text-slate-500 flex items-center justify-between">
          <span>{isEs ? "🔒 Pago procesado por MercadoPago" : "🔒 Payment processed by MercadoPago"}</span>
          {returnUrl && phase !== "success" && (
            <a
              href={returnUrl}
              className="text-sky-600 hover:text-sky-800"
              data-testid="mp-checkout-return-link"
            >
              {isEs ? "Ver detalle de cobro" : "View payment details"}
            </a>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
